import { useEffect, useRef, useState } from 'react';
import { Search, Settings, LogOut } from 'lucide-react';
import { cn } from '../../lib/cn';
import { clearAuth } from '../../lib/auth';
import { NotificationBell } from '../shared/NotificationBell';

interface HeaderProps {
  onNavigate: (view: string) => void;
  onSearch?: (query: string) => void;
}

export function Header({ onNavigate, onSearch }: HeaderProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
        setTimeout(() => searchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch?.(searchQuery.trim());
      onNavigate('search');
      setSearchOpen(false);
    }
  }

  function handleLogout() {
    clearAuth();
    window.location.reload();
  }

  return (
    <header className="flex h-14 items-center justify-between border-b border-surface-200 bg-white px-6">
      {/* Left: Title area */}
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-slate-800">Zachary CRM</h1>
      </div>

      {/* Center: Search */}
      <div className="flex flex-1 justify-center px-8">
        <form onSubmit={handleSearchSubmit} className="w-full max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              placeholder="Search contacts, organizations, deals..."
              className={cn(
                'input pl-9 pr-12',
                searchOpen ? 'ring-1 ring-brand-500 border-brand-500' : '',
              )}
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-surface-300 bg-surface-50 px-1.5 py-0.5 text-2xs font-medium text-slate-400">
              Ctrl+K
            </kbd>
          </div>
        </form>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        <NotificationBell onNavigate={onNavigate} />

        <button
          onClick={() => onNavigate('settings')}
          className="btn-ghost rounded-full p-2"
          title="Settings"
        >
          <Settings className="h-4.5 w-4.5 text-slate-500" />
        </button>

        <button
          onClick={handleLogout}
          className="btn-ghost rounded-full p-2"
          title="Sign out"
        >
          <LogOut className="h-4.5 w-4.5 text-slate-500" />
        </button>
      </div>
    </header>
  );
}
